import { useMemo } from 'react'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import { BaseWidget } from './BaseWidget'
import { PeriodOverrideControl } from '../controls/PeriodOverrideControl'
import { SelectControl } from '../controls/SelectControl'
import { NumberControl } from '../controls/NumberControl'
import useSharedCollection from '../../../../firebase/useSharedCollection'
import useSharedCollectionWhere from '../../../../firebase/useSharedCollectionWhere'
import useSettings from '../../../../hooks/Settings'
import HouseVisitsReportTableVisualization from '../../../dataVisualizations/HouseVisitsReportTableVisualization'
import {
    aggregateHouseVisitsByArea,
} from '../../../../helpers/liveReportHouseVisits/aggregateHouseVisitReportData'
import ModulePlaceholder from './_shared/ModulePlaceholder'
import isTemplateMode from './_shared/isTemplateMode'
import useWidgetPeriod from './_shared/useWidgetPeriod'

/**
 * @extends {BaseWidget}
 */
export class HouseVisitsAreaWidget extends BaseWidget {
    getName() { return 'Bezoeken per gebied' }
    getCategory() { return 'huishoudens' }
    getRequiredModule() { return 'housevisits' }
    getIcon() { return <MapOutlinedIcon /> }

    getControls() {
        return {
            dateRange: new PeriodOverrideControl({
                label: 'Periode',
                description: 'Welke periode de gebiedstelling toont.',
                defaultValue: { mode: 'inherit', start: null, end: null },
            }),
            groupBy: new SelectControl({
                label: 'Gebied',
                description: 'Het niveau waarop adressen worden gegroepeerd.',
                defaultValue: 'postcode6',
                options: {
                    choices: [
                        { value: 'postcode6', label: 'Postcode 6' },
                        { value: 'wijk', label: 'Wijk' },
                        { value: 'buurt', label: 'Buurt' },
                    ],
                },
            }),
            countBy: new SelectControl({
                label: 'Telling',
                description: 'Tel unieke afgeronde adressen of alle bezoeken.',
                defaultValue: 'addresses',
                options: {
                    choices: [
                        { value: 'addresses', label: 'Afgeronde adressen' },
                        { value: 'visits', label: 'Alle bezoeken' },
                    ],
                },
            }),
            limit: new NumberControl({
                label: 'Aantal regels',
                description: 'Maximaal aantal regels in de tabel.',
                defaultValue: 8,
                options: { min: 1, max: 30, step: 1 },
            }),
        }
    }

    render(props) {
        return <Renderer {...props} />
    }
}

/**
 * @param {object} props
 * @param {{mode?: string, start: string|null, end: string|null}} [props.dateRange]
 * @param {'postcode6'|'wijk'|'buurt'} [props.groupBy]
 * @param {'addresses'|'visits'} [props.countBy]
 * @param {number} [props.limit]
 * @returns {JSX.Element}
 */
const Renderer = ({ dateRange, groupBy = 'postcode6', countBy = 'addresses', limit = 8 }) => {
    const { housevisits } = useSettings()
    const isTemplate = isTemplateMode()
    const addresses = useSharedCollection('Adresses')
    const tasks = useSharedCollectionWhere('Tasks', 'Type', 'addressVisit')
    const { range } = useWidgetPeriod(dateRange)

    const rows = useMemo(() => aggregateHouseVisitsByArea({
        addresses,
        tasks,
        range,
        groupBy,
        countBy,
        onlyCompleted: true,
        limit,
    }), [addresses, tasks, range, groupBy, countBy, limit])

    if (isTemplate) return <ModulePlaceholder mode="template" />
    if (housevisits !== true) return <ModulePlaceholder mode="disabled" moduleLabel="Huishoudens" />

    const labelHeader = groupBy === 'postcode6'
        ? 'Postcode'
        : groupBy === 'wijk'
            ? 'Wijk'
            : 'Buurt'

    return (
        <HouseVisitsReportTableVisualization
            rows={rows}
            labelHeader={labelHeader}
            countHeader={countBy === 'visits' ? 'Bezoeken' : 'Adressen'}
            emptyMessage="Geen adressen met locatie gevonden."
        />
    )
}
